import React, { useRef } from "react";
import { FormControl, Grid, MenuItem, Select, Switch } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SettingsCard from "./SettingsCard";
import { AiFillFire, AiFillStar, AiOutlineThunderbolt } from "react-icons/ai";
import {
  FaBookmark,
  FaCrown,
  FaGem,
  FaMapMarkerAlt,
  FaMedal,
  FaTrophy,
} from "react-icons/fa";
import { MdVerified } from "react-icons/md";
import { HiSparkles } from "react-icons/hi2";

const LISTINGS_PER_PAGE_OPTIONS = ["3", "6", "9", "12"];
/** Category / filter / directory listing pages (menu → category). */
const DIRECTORY_LISTINGS_PER_PAGE_OPTIONS = ["6", "9", "12", "18", "24", "36", "48"];
const BADGE_DEFAULT_BG = "#ef4444";
const BADGE_DEFAULT_TEXT = "#ffffff";
const CLAIMED_BADGE_DEFAULT_BG = "#2563eb";

/** Must match theme3 PremiumListingBadge BADGE_ICONS keys (except none → null). */
const BADGE_ICON_MAP = {
  bolt: AiOutlineThunderbolt,
  star: AiFillStar,
  crown: FaCrown,
  verified: MdVerified,
  flame: AiFillFire,
  sparkle: HiSparkles,
  medal: FaMedal,
  trophy: FaTrophy,
  gem: FaGem,
  pin: FaMapMarkerAlt,
  bookmark: FaBookmark,
  none: null,
};

const BADGE_ICON_OPTIONS = [
  { value: "bolt", label: "Lightning" },
  { value: "star", label: "Star" },
  { value: "crown", label: "Crown" },
  { value: "verified", label: "Verified" },
  { value: "flame", label: "Flame" },
  { value: "sparkle", label: "Sparkles" },
  { value: "medal", label: "Medal" },
  { value: "trophy", label: "Trophy" },
  { value: "gem", label: "Gem" },
  { value: "pin", label: "Pin" },
  { value: "bookmark", label: "Bookmark" },
  { value: "none", label: "None" },
];

/** Same height for selects, badge text field, and color pickers */
const LISTINGS_CONTROL_HEIGHT = 42;

function normalizeColor(value, fallback) {
  const s = (value || "").trim();
  if (/^#[0-9A-Fa-f]{6}$/i.test(s)) return s;
  if (/^#[0-9A-Fa-f]{3}$/i.test(s)) {
    const r = s[1];
    const g = s[2];
    const b = s[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return fallback;
}

const DROPDOWN_STYLE = {
  width: "100%",
  height: LISTINGS_CONTROL_HEIGHT,
  minHeight: LISTINGS_CONTROL_HEIGHT,
  boxSizing: "border-box",
  padding: "0 12px",
  borderRadius: "8px",
  border: "1px solid #D2D6DA",
  fontSize: "14px",
  lineHeight: `${LISTINGS_CONTROL_HEIGHT - 2}px`,
  fontFamily: "inherit",
  backgroundColor: "white",
  cursor: "pointer",
};

const COLOR_INPUT_STYLE = {
  ...DROPDOWN_STYLE,
  height: LISTINGS_CONTROL_HEIGHT,
  minHeight: LISTINGS_CONTROL_HEIGHT,
  padding: 4,
  width: "100%",
  maxWidth: 200,
  lineHeight: 1,
};

/** Color pickers in the badge row: full width of column (no 200px cap). */
const BADGE_ROW_COLOR_INPUT_STYLE = {
  ...COLOR_INPUT_STYLE,
  maxWidth: "100%",
};

/** Applied to SoftInput root (same 42px as selects / color inputs) */
const BADGE_INNER_LINE = LISTINGS_CONTROL_HEIGHT - 2;
const BADGE_TEXT_FIELD_SX = {
  minHeight: LISTINGS_CONTROL_HEIGHT,
  maxHeight: LISTINGS_CONTROL_HEIGHT,
  height: LISTINGS_CONTROL_HEIGHT,
  boxSizing: "border-box",
  alignItems: "center",
  py: "0 !important",
  pt: "0 !important",
  pb: "0 !important",
  "& .MuiInputBase-input": {
    py: "0 !important",
    pt: "0 !important",
    pb: "0 !important",
    px: "12px !important",
    minHeight: 0,
    height: BADGE_INNER_LINE,
    boxSizing: "border-box",
    lineHeight: `${BADGE_INNER_LINE}px`,
    "&::placeholder": {
      lineHeight: `${BADGE_INNER_LINE}px`,
      opacity: 0.55,
    },
  },
};

/**
 * Soft UI theme sets OutlinedInput root padding 8px 28px 8px 12px !important and
 * MuiInputBase height auto !important — must override on Select root (sx merges here).
 */
const BADGE_ICON_SELECT_SX = {
  boxSizing: "border-box",
  height: `${LISTINGS_CONTROL_HEIGHT}px !important`,
  minHeight: `${LISTINGS_CONTROL_HEIGHT}px !important`,
  maxHeight: `${LISTINGS_CONTROL_HEIGHT}px !important`,
  padding: `0 32px 0 12px !important`,
  display: "flex !important",
  alignItems: "center",
  borderRadius: "8px",
  fontSize: "14px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#D2D6DA",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#b0b8c1",
  },
  "& .MuiOutlinedInput-input": {
    padding: "0 !important",
    minHeight: "0 !important",
    height: `${BADGE_INNER_LINE}px !important`,
    display: "flex !important",
    alignItems: "center",
  },
  /* Theme MuiSelect sets display: grid on .MuiSelect-select */
  "& .MuiSelect-select": {
    display: "flex !important",
    flexDirection: "row",
    flexWrap: "nowrap",
    alignItems: "center",
    gap: "6px",
    boxSizing: "border-box",
    height: `${BADGE_INNER_LINE}px !important`,
    minHeight: `${BADGE_INNER_LINE}px !important`,
    maxHeight: `${BADGE_INNER_LINE}px !important`,
    padding: "0 !important",
    whiteSpace: "nowrap",
    lineHeight: 1,
    "& svg": {
      display: "block",
      flexShrink: 0,
    },
  },
  /* Global theme hides Select icon; show it here for affordance */
  "& .MuiSelect-icon": {
    display: "block !important",
    top: "50%",
    transform: "translateY(-50%)",
    right: 8,
  },
};

function renderBadgeIconSelectValue(value) {
  const opt = BADGE_ICON_OPTIONS.find((o) => o.value === value) || BADGE_ICON_OPTIONS[0];
  const IconComp = BADGE_ICON_MAP[opt.value];
  return (
    <SoftBox
      sx={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        alignItems: "center",
        gap: "6px",
        minWidth: 0,
        width: "100%",
        maxHeight: BADGE_INNER_LINE,
        overflow: "hidden",
        lineHeight: 1,
      }}
    >
      {IconComp ? (
        <SoftBox
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            lineHeight: 0,
            color: "text.secondary",
          }}
        >
          <IconComp size={16} />
        </SoftBox>
      ) : null}
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{opt.label}</span>
    </SoftBox>
  );
}

const PlaceholderCard = ({
  inputData,
  autoSaveField,
  showPremiumListingBadge,
  showClaimedListingBadge,
  handleShowPremiumListingBadgeChange,
  handleShowClaimedListingBadgeChange,
}) => {
  const premiumListingBadgeIconRef = useRef(null);
  const claimedListingBadgeIconRef = useRef(null);

  const save = (e) => {
    if (autoSaveField) autoSaveField(e.target.name, e.target.value);
  };

  const badgeLabelDefault = inputData?.premium_listing_badge_label || "";
  const badgeBgDefault = normalizeColor(
    inputData?.premium_listing_badge_bg,
    BADGE_DEFAULT_BG
  );
  const badgeTextDefault = normalizeColor(
    inputData?.premium_listing_badge_text_color,
    BADGE_DEFAULT_TEXT
  );
  const badgeIconDefault =
    typeof inputData?.premium_listing_badge_icon === "string" &&
    BADGE_ICON_OPTIONS.some((o) => o.value === inputData.premium_listing_badge_icon)
      ? inputData.premium_listing_badge_icon
      : "bolt";

  const claimedBadgeLabelDefault = inputData?.claimed_listing_badge_label || "";
  const claimedBadgeBgDefault = normalizeColor(
    inputData?.claimed_listing_badge_bg,
    CLAIMED_BADGE_DEFAULT_BG
  );
  const claimedBadgeTextDefault = normalizeColor(
    inputData?.claimed_listing_badge_text_color,
    BADGE_DEFAULT_TEXT
  );
  const claimedBadgeIconDefault =
    typeof inputData?.claimed_listing_badge_icon === "string" &&
    BADGE_ICON_OPTIONS.some((o) => o.value === inputData.claimed_listing_badge_icon)
      ? inputData.claimed_listing_badge_icon
      : "verified";

  return (
    <SettingsCard
      title="Listings Display Settings"
      subtitle="Home featured/free counts, and how many businesses show per page when opening a category from the menu. Featured badge: empty text → “Featured”; default colors → red badge. Claimed badge: empty text → “Claimed”; default colors → blue badge. Icon presets or None for text-only."
      gridProps={{
        xs: 12,
        sx: {
          "@media (min-width: 1100px)": {
            flexBasis: "calc(50% - 12px)",
            maxWidth: "calc(50% - 12px)",
            width: "calc(50% - 12px)",
            marginRight: "24px"
          }
        }
      }}
    >
      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <SoftBox display="flex" flexDirection="column">
            <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
              <SoftTypography
                component="label"
                variant="caption"
                fontWeight="bold"
                textTransform="capitalize"
              >
                Featured / page
              </SoftTypography>
            </SoftBox>
            <select
              name="featured_listings_per_page"
              defaultValue={inputData?.featured_listings_per_page || "3"}
              style={DROPDOWN_STYLE}
              onChange={save}
            >
              {LISTINGS_PER_PAGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </SoftBox>
        </Grid>
        <Grid item xs={12}>
          <SoftBox display="flex" flexDirection="column">
            <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
              <SoftTypography
                component="label"
                variant="caption"
                fontWeight="bold"
                textTransform="capitalize"
              >
                Free / page
              </SoftTypography>
            </SoftBox>
            <select
              name="free_listings_per_page"
              defaultValue={inputData?.free_listings_per_page || "3"}
              style={DROPDOWN_STYLE}
              onChange={save}
            >
              {LISTINGS_PER_PAGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </SoftBox>
        </Grid>
        <Grid item xs={12}>
          <SoftBox display="flex" flexDirection="column">
            <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
              <SoftTypography
                component="label"
                variant="caption"
                fontWeight="bold"
                textTransform="capitalize"
              >
                Category / page
              </SoftTypography>
            </SoftBox>
            <select
              name="directory_listings_per_page"
              defaultValue={inputData?.directory_listings_per_page || "6"}
              style={DROPDOWN_STYLE}
              onChange={save}
            >
              {DIRECTORY_LISTINGS_PER_PAGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </SoftBox>
        </Grid>

        <Grid item xs={12}>
          <SoftBox display="flex" alignItems="center" mb={1}>
            <Switch
              checked={showPremiumListingBadge}
              onChange={handleShowPremiumListingBadgeChange}
            />
            <SoftTypography variant="body2" sx={{ marginLeft: "10px", fontSize: "14px" }}>
              Display featured listing badge
            </SoftTypography>
          </SoftBox>
          {showPremiumListingBadge && (
          <Grid container spacing={2.5} alignItems="flex-start">
            <Grid item xs={12} sm={6} lg={3}>
              <SoftBox display="flex" flexDirection="column" width="100%">
                <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                  <SoftTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                    textTransform="capitalize"
                    htmlFor="premium_listing_badge_label"
                  >
                    Badge text
                  </SoftTypography>
                </SoftBox>
                <SoftInput
                  id="premium_listing_badge_label"
                  name="premium_listing_badge_label"
                  defaultValue={badgeLabelDefault}
                  placeholder="Featured, Sponsored, Verified…"
                  inputProps={{ maxLength: 20 }}
                  sx={BADGE_TEXT_FIELD_SX}
                  onBlur={save}
                />
              </SoftBox>
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <SoftBox display="flex" flexDirection="column" width="100%">
                <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                  <SoftTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                    textTransform="capitalize"
                    htmlFor="premium_listing_badge_icon"
                  >
                    Icon
                  </SoftTypography>
                </SoftBox>
                <input
                  ref={premiumListingBadgeIconRef}
                  type="hidden"
                  name="premium_listing_badge_icon"
                  defaultValue={badgeIconDefault}
                />
                <FormControl
                  fullWidth
                  size="small"
                  variant="outlined"
                  sx={{ width: "100%", "& .MuiInputBase-root": { marginTop: 0 } }}
                >
                  <Select
                    id="premium_listing_badge_icon"
                    defaultValue={badgeIconDefault}
                    aria-label="Premium listing badge icon"
                    renderValue={renderBadgeIconSelectValue}
                    onChange={(e) => {
                      if (premiumListingBadgeIconRef.current) {
                        premiumListingBadgeIconRef.current.value = e.target.value;
                      }
                      if (autoSaveField) {
                        autoSaveField("premium_listing_badge_icon", e.target.value);
                      }
                    }}
                    MenuProps={{
                      disableScrollLock: true,
                      PaperProps: { sx: { maxHeight: 320 } },
                    }}
                    sx={BADGE_ICON_SELECT_SX}
                  >
                    {BADGE_ICON_OPTIONS.map((opt) => {
                      const IconComp = BADGE_ICON_MAP[opt.value];
                      return (
                        <MenuItem
                          key={opt.value}
                          value={opt.value}
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            flexWrap: "nowrap",
                            alignItems: "center",
                            gap: "8px",
                            minHeight: LISTINGS_CONTROL_HEIGHT,
                            "& svg": { display: "block", flexShrink: 0 },
                          }}
                        >
                          <SoftBox
                            component="span"
                            sx={{
                              display: "inline-flex",
                              width: 22,
                              justifyContent: "center",
                              flexShrink: 0,
                              lineHeight: 0,
                              color: "text.secondary",
                            }}
                          >
                            {IconComp ? <IconComp size={16} /> : null}
                          </SoftBox>
                          <span style={{ whiteSpace: "nowrap" }}>{opt.label}</span>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </SoftBox>
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <SoftBox display="flex" flexDirection="column" width="100%">
                <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                  <SoftTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                    textTransform="capitalize"
                  >
                    Background
                  </SoftTypography>
                </SoftBox>
                <input
                  name="premium_listing_badge_bg"
                  type="color"
                  defaultValue={badgeBgDefault}
                  aria-label="Badge background color"
                  style={BADGE_ROW_COLOR_INPUT_STYLE}
                  onBlur={save}
                />
              </SoftBox>
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <SoftBox display="flex" flexDirection="column" width="100%">
                <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                  <SoftTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                    textTransform="capitalize"
                  >
                    Text & icon
                  </SoftTypography>
                </SoftBox>
                <input
                  name="premium_listing_badge_text_color"
                  type="color"
                  defaultValue={badgeTextDefault}
                  aria-label="Badge text and icon color"
                  style={BADGE_ROW_COLOR_INPUT_STYLE}
                  onBlur={save}
                />
              </SoftBox>
            </Grid>
          </Grid>
          )}
        </Grid>

        <Grid item xs={12}>
          <SoftBox display="flex" alignItems="center" mb={1}>
            <Switch
              checked={showClaimedListingBadge}
              onChange={handleShowClaimedListingBadgeChange}
            />
            <SoftTypography variant="body2" sx={{ marginLeft: "10px", fontSize: "14px" }}>
              Display claimed listing badge
            </SoftTypography>
          </SoftBox>
          {showClaimedListingBadge && (
          <Grid container spacing={2.5} alignItems="flex-start">
            <Grid item xs={12} sm={6} lg={3}>
              <SoftBox display="flex" flexDirection="column" width="100%">
                <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                  <SoftTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                    textTransform="capitalize"
                    htmlFor="claimed_listing_badge_label"
                  >
                    Badge text
                  </SoftTypography>
                </SoftBox>
                <SoftInput
                  id="claimed_listing_badge_label"
                  name="claimed_listing_badge_label"
                  defaultValue={claimedBadgeLabelDefault}
                  placeholder="Claimed, Verified, Owner…"
                  inputProps={{ maxLength: 20 }}
                  sx={BADGE_TEXT_FIELD_SX}
                  onBlur={save}
                />
              </SoftBox>
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <SoftBox display="flex" flexDirection="column" width="100%">
                <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                  <SoftTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                    textTransform="capitalize"
                    htmlFor="claimed_listing_badge_icon"
                  >
                    Icon
                  </SoftTypography>
                </SoftBox>
                <input
                  ref={claimedListingBadgeIconRef}
                  type="hidden"
                  name="claimed_listing_badge_icon"
                  defaultValue={claimedBadgeIconDefault}
                />
                <FormControl
                  fullWidth
                  size="small"
                  variant="outlined"
                  sx={{ width: "100%", "& .MuiInputBase-root": { marginTop: 0 } }}
                >
                  <Select
                    id="claimed_listing_badge_icon"
                    defaultValue={claimedBadgeIconDefault}
                    aria-label="Claimed listing badge icon"
                    renderValue={renderBadgeIconSelectValue}
                    onChange={(e) => {
                      if (claimedListingBadgeIconRef.current) {
                        claimedListingBadgeIconRef.current.value = e.target.value;
                      }
                      if (autoSaveField) {
                        autoSaveField("claimed_listing_badge_icon", e.target.value);
                      }
                    }}
                    MenuProps={{
                      disableScrollLock: true,
                      PaperProps: { sx: { maxHeight: 320 } },
                    }}
                    sx={BADGE_ICON_SELECT_SX}
                  >
                    {BADGE_ICON_OPTIONS.map((opt) => {
                      const IconComp = BADGE_ICON_MAP[opt.value];
                      return (
                        <MenuItem
                          key={opt.value}
                          value={opt.value}
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            flexWrap: "nowrap",
                            alignItems: "center",
                            gap: "8px",
                            minHeight: LISTINGS_CONTROL_HEIGHT,
                            "& svg": { display: "block", flexShrink: 0 },
                          }}
                        >
                          <SoftBox
                            component="span"
                            sx={{
                              display: "inline-flex",
                              width: 22,
                              justifyContent: "center",
                              flexShrink: 0,
                              lineHeight: 0,
                              color: "text.secondary",
                            }}
                          >
                            {IconComp ? <IconComp size={16} /> : null}
                          </SoftBox>
                          <span style={{ whiteSpace: "nowrap" }}>{opt.label}</span>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </SoftBox>
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <SoftBox display="flex" flexDirection="column" width="100%">
                <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                  <SoftTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                    textTransform="capitalize"
                  >
                    Background
                  </SoftTypography>
                </SoftBox>
                <input
                  name="claimed_listing_badge_bg"
                  type="color"
                  defaultValue={claimedBadgeBgDefault}
                  aria-label="Claimed badge background color"
                  style={BADGE_ROW_COLOR_INPUT_STYLE}
                  onBlur={save}
                />
              </SoftBox>
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <SoftBox display="flex" flexDirection="column" width="100%">
                <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                  <SoftTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                    textTransform="capitalize"
                  >
                    Text & icon
                  </SoftTypography>
                </SoftBox>
                <input
                  name="claimed_listing_badge_text_color"
                  type="color"
                  defaultValue={claimedBadgeTextDefault}
                  aria-label="Claimed badge text and icon color"
                  style={BADGE_ROW_COLOR_INPUT_STYLE}
                  onBlur={save}
                />
              </SoftBox>
            </Grid>
          </Grid>
          )}
        </Grid>
      </Grid>
    </SettingsCard>
  );
};

export default PlaceholderCard;
